import React from 'react';
import { GeneratedCredential } from '../../types';

interface GeneratedCredentialsModalProps {
  credentials: GeneratedCredential[];
  onClose: () => void;
}

const GeneratedCredentialsModal: React.FC<GeneratedCredentialsModalProps> = ({ credentials, onClose }) => {

  const handleDownloadCsv = () => {
    // 1. Create CSV header
    const headers = ['FullName', 'Username', 'Password'];
    const csvRows = [headers.join(',')];

    // 2. Create a row for each credential, escaping commas by quoting
    for (const cred of credentials) {
      const fullName = `"${cred.fullName.replace(/"/g, '""')}"`;
      const username = `"${cred.username}"`;
      const password = `"${cred.password || ''}"`; // Ensure password is not undefined
      csvRows.push([fullName, username, password].join(','));
    }

    const csvString = csvRows.join('\n');
    
    // 3. Create a Blob and trigger download
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'student-credentials.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
    
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 border-b pb-4">
          <h2 className="text-2xl font-bold text-gray-800">Student Accounts Created</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-3xl">&times;</button>
        </div>
        <div className="p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 mb-4 rounded-r-lg">
            <h4 className="font-bold">Important!</h4>
            <p>Please download and save these temporary passwords. They will not be shown again.</p>
        </div>
        <div className="overflow-y-auto flex-grow mb-4">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Temporary Password</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {credentials.map((cred, index) => (
                        <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{cred.fullName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{cred.username}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{cred.password}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        <div className="flex justify-end space-x-4">
            <button onClick={handleDownloadCsv} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                Download as CSV
            </button>
            <button onClick={onClose} className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600">Close</button>
        </div>
      </div>
    </div>
  );
};

export default GeneratedCredentialsModal;
